import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { api } from "@/lib/apiClient";
import { parseBarcode } from "@/hooks/useBarcodeScanner";
import type { MaterialInventory, RawMaterial } from "@/types/legacy";
import { toast } from "sonner";

interface LookupResult {
  found: boolean;
  source: "material_inventory" | "raw_materials" | null;
  material: MaterialInventory | null;
  rawMaterial: RawMaterial | null;
  scannedCode: string;
}

interface BarcodeGlobalState {
  isSearching: boolean;
  lastResult: LookupResult | null;
  showPanel: boolean;
  error: string | null;
}

interface BarcodeGlobalContextValue {
  state: BarcodeGlobalState;
  closePanel: () => void;
  openPanelWithResult: (result: LookupResult) => void;
}

const defaultState: BarcodeGlobalState = {
  isSearching: false,
  lastResult: null,
  showPanel: false,
  error: null,
};

const BarcodeGlobalContext = createContext<BarcodeGlobalContextValue>({
  state: defaultState,
  closePanel: () => {},
  openPanelWithResult: () => {},
});

export const useBarcodeGlobal = () => useContext(BarcodeGlobalContext);

interface BarcodeGlobalProviderProps {
  children: ReactNode;
}

export function BarcodeGlobalProvider({ children }: BarcodeGlobalProviderProps) {
  const [state, setState] = useState<BarcodeGlobalState>(defaultState);

  // Buffer for detecting rapid scanner input
  const inputBuffer = useRef<string>("");
  const lastKeyTime = useRef<number>(0);
  const scanTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Lookup material by code in both tables
   */
  const lookupMaterial = useCallback(
    async (code: string): Promise<LookupResult> => {
      const emptyResult: LookupResult = {
        found: false,
        source: null,
        material: null,
        rawMaterial: null,
        scannedCode: code,
      };

      if (!code || code.trim().length === 0) {
        return emptyResult;
      }

      try {
        // Search in material_inventory first
        const { data: materials, error: matError } = await api
          .from("material_inventory")
          .select("*")
          .eq("code", code.trim())
          .eq("is_active", true);

        if (!matError && materials && materials.length > 0) {
          return {
            found: true,
            source: "material_inventory",
            material: materials[0] as MaterialInventory,
            rawMaterial: null,
            scannedCode: code,
          };
        }

        // Search in raw_materials
        const { data: rawMaterials, error: rawError } = await api
          .from("raw_materials")
          .select("*")
          .eq("code", code.trim())
          .eq("is_active", true);

        if (!rawError && rawMaterials && rawMaterials.length > 0) {
          return {
            found: true,
            source: "raw_materials",
            rawMaterial: rawMaterials[0] as RawMaterial,
            material: null,
            scannedCode: code,
          };
        }

        return emptyResult;
      } catch (err) {
        console.error("Error looking up material:", err);
        return emptyResult;
      }
    },
    []
  );

  /**
   * Process a completed scan
   */
  const processScan = useCallback(
    async (scannedValue: string) => {
      const parsed = parseBarcode(scannedValue);
      if (!parsed) {
        setState((prev) => ({
          ...prev,
          error: "Codigo de barras invalido",
          isSearching: false,
        }));
        return;
      }

      setState((prev) => ({ ...prev, isSearching: true, error: null }));

      const result = await lookupMaterial(parsed.code);

      setState((prev) => ({
        ...prev,
        isSearching: false,
        lastResult: result,
        showPanel: true,
        error: result.found ? null : `Material no encontrado: ${parsed.code}`,
      }));

      if (result.found) {
        const name = result.material?.name || result.rawMaterial?.name || "";
        toast.success(`Material encontrado: ${name}`);
      } else {
        toast.error(`Material no encontrado: ${parsed.code}`);
      }
    },
    [lookupMaterial]
  );

  /**
   * Global keydown handler for detecting barcode scanner input
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if an input/textarea is focused (user is typing)
      const activeEl = document.activeElement;
      const isInputActive =
        activeEl?.tagName === "INPUT" ||
        activeEl?.tagName === "TEXTAREA" ||
        (activeEl as HTMLElement)?.isContentEditable;

      if (isInputActive) return;

      // Con un diálogo/formulario abierto, el escaneo pertenece a ese formulario:
      // el panel global no debe capturarlo ni navegar fuera de la pantalla actual.
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;

      const now = Date.now();
      const timeDiff = now - lastKeyTime.current;

      // If more than 100ms since last key, reset buffer
      // This distinguishes normal typing from scanner input
      if (timeDiff > 100) {
        inputBuffer.current = "";
      }

      // Handle Enter key - process the buffer
      if (e.key === "Enter" && inputBuffer.current.length > 0) {
        e.preventDefault();
        const value = inputBuffer.current.trim();
        inputBuffer.current = "";

        // Clear any pending timeout
        if (scanTimeout.current) {
          clearTimeout(scanTimeout.current);
          scanTimeout.current = null;
        }

        processScan(value);
        return;
      }

      // Accumulate printable characters
      if (e.key.length === 1) {
        inputBuffer.current += e.key;
        lastKeyTime.current = now;

        // Set a timeout to process in case Enter isn't received
        if (scanTimeout.current) {
          clearTimeout(scanTimeout.current);
        }

        scanTimeout.current = setTimeout(() => {
          const value = inputBuffer.current.trim();
          if (value.length >= 3) {
            // Minimum code length
            processScan(value);
          }
          inputBuffer.current = "";
        }, 200);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (scanTimeout.current) {
        clearTimeout(scanTimeout.current);
      }
    };
  }, [processScan]);

  const closePanel = useCallback(() => {
    setState((prev) => ({
      ...prev,
      showPanel: false,
      lastResult: null,
      error: null,
    }));
  }, []);

  const openPanelWithResult = useCallback((result: LookupResult) => {
    setState((prev) => ({
      ...prev,
      lastResult: result,
      showPanel: true,
      error: result.found ? null : `Material no encontrado: ${result.scannedCode}`,
    }));
  }, []);

  return (
    <BarcodeGlobalContext.Provider
      value={{ state, closePanel, openPanelWithResult }}
    >
      {children}
    </BarcodeGlobalContext.Provider>
  );
}

export { BarcodeGlobalContext };
